/**
 * Compact uploading/processing state component for Public Recording
 *
 * Upload phase: Gradient arc that sweeps around a cloud-upload icon,
 *   with small data particles rising upward — a clear, polished
 *   "your audio is being sent" visual.
 *
 * Processing phase: Sparkle icon with orbital ring and shimmer sweep.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

interface CompactUploadingStateProps {
  phase?: 'uploading' | 'processing';
}

/**
 * Upload animation: filling arc + cloud-upload icon + rising particles
 */
const UploadIcon = () => {
  // 3 particles that continuously rise and fade
  const particles = [
    { delay: 0, x: -4, size: 3 },
    { delay: 0.6, x: 1, size: 2.5 },
    { delay: 1.2, x: 5, size: 2 },
  ];

  return (
    <div className="relative w-11 h-11 flex items-center justify-center">
      {/* Background track ring */}
      <svg
        className="absolute inset-0"
        width="44"
        height="44"
        viewBox="0 0 44 44"
        fill="none">
        <circle
          cx="22"
          cy="22"
          r="19"
          strokeWidth="2.5"
          className="stroke-purple-100 dark:stroke-purple-900/50"
        />
      </svg>

      {/* Animated gradient arc that sweeps around */}
      <motion.svg
        className="absolute inset-0"
        width="44"
        height="44"
        viewBox="0 0 44 44"
        fill="none"
        animate={{ rotate: 360 }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}>
        <defs>
          <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.1" />
            <stop offset="40%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
        <circle
          cx="22"
          cy="22"
          r="19"
          stroke="url(#arcGrad)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="85 35"
          fill="none"
        />
      </motion.svg>

      {/* Inner circle background */}
      <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/60 dark:to-indigo-950/60 flex items-center justify-center border border-purple-200/50 dark:border-purple-800/50">
        {/* Cloud upload icon — inline SVG for precise control */}
        <motion.svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-purple-600 dark:text-purple-400"
          animate={{ y: [0, -1.5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
          {/* Cloud shape */}
          <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
          {/* Upload arrow */}
          <path d="M12 12v9" />
          <path d="m8 16 4-4 4 4" />
        </motion.svg>
      </div>

      {/* Rising data particles */}
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-purple-400 dark:bg-purple-300"
          style={{
            width: p.size,
            height: p.size,
            left: `calc(50% + ${p.x}px)`,
          }}
          animate={{
            y: [6, -16],
            opacity: [0, 0.8, 0],
            scale: [0.5, 1, 0.3],
          }}
          transition={{
            duration: 1.4,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
};

/**
 * Processing phase icon: sparkle with orbital ring and shimmer
 */
const ProcessingIcon = () => (
  <div className="relative w-11 h-11 flex items-center justify-center">
    {/* Orbital ring */}
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-dashed border-purple-300 dark:border-purple-700"
      animate={{ rotate: 360 }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
    />

    {/* Inner circle with shimmer */}
    <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/40 dark:to-indigo-900/40 flex items-center justify-center overflow-hidden border border-purple-200/60 dark:border-purple-700/60">
      <motion.div
        animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
        <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
      </motion.div>

      {/* Shimmer sweep */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 dark:via-white/10 to-transparent"
        animate={{ x: ['-100%', '100%'] }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut',
          repeatDelay: 0.6,
        }}
      />
    </div>
  </div>
);

export const CompactUploadingState = ({
  phase = 'uploading',
}: CompactUploadingStateProps) => {
  const isProcessing = phase === 'processing';

  return (
    <motion.div
      key={`upload-${phase}`}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{ maxHeight: '48px' }}
      className="relative w-full h-full flex items-center justify-center px-3">
      <div className="relative z-10 flex items-center max-w-7xl">
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <button
              className="flex items-center gap-3 cursor-help focus:outline-none pointer-events-auto"
              type="button"
              aria-label={isProcessing ? 'Processing status' : 'Upload status'}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}>
                {isProcessing ? <ProcessingIcon /> : <UploadIcon />}
              </motion.div>

              {/* Inline label */}
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                {isProcessing ? 'Processing...' : 'Uploading...'}
              </motion.span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            sideOffset={8}
            avoidCollisions={false}
            className="z-50">
            <p>
              {isProcessing
                ? 'AI is processing your recording...'
                : 'Uploading your recording...'}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
};
